/**
 * Weekly Digest — admin-only read surface at /admin/digest. B.2 Phase 2.
 *
 * Renders weekly_digests rows (assembled by the weekly-digest Edge Function,
 * GH cron Mon 07:00 UTC): a weeks list (left) + the selected week's payload
 * (right), rendered by a GENERIC section→items walker so digest content can
 * evolve EF-side only — "build the frame, content morphs live" (Sol's B.2
 * ratification). Items may carry an internal link (deep-links into
 * /admin/errors, /admin/builds, /admin/feedback).
 *
 * Fetch failures render an EXPLICIT error state, never fake-empty (the
 * R5-F16 lesson: a query failure masquerading as "no digests yet" is the
 * exact silent-failure class B.2 exists to close).
 */

import { useState, useEffect, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { CalendarRange, AlertTriangle, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface DigestItem {
  label: string;
  value?: string;
  severity?: 'info' | 'warning' | 'critical';
  link?: string;
}
interface DigestSection {
  key: string;
  title: string;
  summary: string;
  items: DigestItem[];
}
interface DigestPayload {
  version: number;
  period: { start: string; end: string };
  sections: DigestSection[];
}
interface DigestRow {
  id: string;
  period_start: string;
  period_end: string;
  assembled_at: string;
  email_sent: boolean;
  pushover_sent: boolean;
  payload: DigestPayload | null;
}

const SEVERITY_CLASS: Record<string, string> = {
  warning: 'text-amber-400',
  critical: 'text-red-400',
  info: 'text-ink-secondary',
};

export default function WeeklyDigest(): JSX.Element {
  useDocumentTitle('Weekly Digest');
  const [rows, setRows] = useState<DigestRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetchDigests = async (): Promise<void> => {
    setLoading(true);
    setFetchError(null);
    const { data, error } = await supabase
      .from('weekly_digests')
      .select('id, period_start, period_end, assembled_at, email_sent, pushover_sent, payload')
      .order('period_start', { ascending: false })
      .limit(52);
    if (error) {
      // Explicit, never fake-empty (R5-F16).
      setFetchError(error.message);
      setRows([]);
    } else {
      const list = (data ?? []) as unknown as DigestRow[];
      setRows(list);
      setSelectedId((prev) => prev ?? list[0]?.id ?? null);
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchDigests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-5xl mx-auto p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink-bright">Weekly Digest</h1>
            <p className="text-xs text-ink-muted">
              The informational bucket — assembled Mondays 07:00 UTC by the weekly-digest Edge
              Function; delivered as email headlines + a Pushover ready-ping.
            </p>
          </div>
          <button
            onClick={() => void fetchDigests()}
            className="flex items-center gap-1.5 text-xs text-ink-secondary hover:text-ink-bright border border-border rounded-md px-2.5 py-1.5"
            aria-label="Refresh digests"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {fetchError && (
          <div className="bg-surface-2 border border-red-500/40 rounded-md p-4 flex items-start gap-2.5">
            <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium text-ink-bright">Failed to load digests</div>
              <div className="text-xs text-ink-secondary mt-0.5 font-mono">{fetchError}</div>
            </div>
          </div>
        )}

        {loading && !fetchError && <div className="text-sm text-ink-muted p-4">Loading digests…</div>}

        {!loading && !fetchError && rows.length === 0 && (
          <div className="bg-surface-2 border border-border rounded-md p-6 text-center">
            <CalendarRange size={20} className="mx-auto text-ink-muted mb-2" />
            <div className="text-sm text-ink-secondary">No digests assembled yet.</div>
            <div className="text-xs text-ink-muted mt-1">
              The first one lands after the weekly-digest cron&apos;s first Monday run (or a
              manual workflow dispatch from GitHub Actions).
            </div>
          </div>
        )}

        {!loading && !fetchError && rows.length > 0 && (
          <div className="flex gap-4 items-start">
            {/* Weeks list */}
            <div className="w-56 shrink-0 space-y-1.5">
              {rows.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={clsx(
                    'w-full text-left px-3 py-2 rounded-md border text-xs',
                    r.id === selectedId
                      ? 'bg-surface-3 border-accent/50 text-ink-bright'
                      : 'bg-surface-2 border-border text-ink-secondary hover:text-ink-bright'
                  )}
                >
                  <div className="font-medium">
                    {r.period_start} → {r.period_end}
                  </div>
                  <div className="text-ink-muted mt-0.5">
                    email {r.email_sent ? '✓' : '✗'} · push {r.pushover_sent ? '✓' : '✗'}
                  </div>
                </button>
              ))}
            </div>

            {/* Selected digest — the generic section walker */}
            <div className="flex-1 space-y-3 min-w-0">
              {selected?.payload?.sections?.length ? (
                selected.payload.sections.map((s) => (
                  <div key={s.key} className="bg-surface-2 border border-border rounded-md p-4">
                    <div className="text-sm font-semibold text-ink-bright">{s.title}</div>
                    <div className="text-xs text-ink-secondary mt-1">{s.summary}</div>
                    {s.items.length > 0 && (
                      <ul className="mt-2.5 space-y-1.5">
                        {s.items.map((item, i) => (
                          <li key={i} className="flex items-baseline gap-2 text-xs">
                            <span className={clsx('shrink-0', SEVERITY_CLASS[item.severity ?? 'info'])}>
                              •
                            </span>
                            <span className="text-ink-secondary min-w-0">
                              {item.link ? (
                                <Link to={item.link} className="text-accent hover:underline">
                                  {item.label}
                                </Link>
                              ) : (
                                item.label
                              )}
                              {item.value && (
                                <span className="text-ink-muted font-mono ml-1.5">{item.value}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              ) : (
                <div className="bg-surface-2 border border-border rounded-md p-4 text-xs text-ink-muted">
                  This digest row carries no renderable sections (payload empty or
                  unrecognized shape).
                </div>
              )}
              {selected && (
                <div className="text-2xs text-ink-muted">
                  Assembled {selected.assembled_at} · digest id {selected.id}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
